import { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lc_admin_token";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  const setToken = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setTokenState(t);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setAdmin(null);
        setChecking(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAdmin(data);
      } catch {
        if (!cancelled) {
          setToken(null);
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { email, password });
    setToken(data.access_token);
    setAdmin({ email: data.email, role: "admin" });
    return data;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await axios.post(`${API}/admin/auth/google-callback`, {
      session_id: sessionId,
    });
    setToken(data.access_token);
    setAdmin({ email: data.email, role: "admin" });
    return data;
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
  };

  // Preconfigured axios for admin calls
  const adminApi = useMemo(() => {
    const inst = axios.create({ baseURL: `${API}/admin` });
    inst.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    inst.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          setToken(null);
          setAdmin(null);
        }
        return Promise.reject(err);
      }
    );
    return inst;
  }, []);

  return (
    <AuthContext.Provider value={{ token, admin, checking, login, loginWithGoogleSession, logout, adminApi }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiError(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  if (typeof d?.msg === "string") return d.msg;
  return fallback;
}
